import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'Product', href: '/product' },
    { name: 'Workflow', href: '/workflow' },
    { name: 'Financial Integration', href: '/financial-integration' },
    { name: 'Tech & Security', href: '/tech-security' },
    { name: 'Acquisition Portal', href: '/acquisition-portal' },
    { name: 'Team', href: '/team' },
    { name: 'Support', href: '/support' }
  ];

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200/70">
      <div className="w-full px-4 sm:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Logo Section */}
          <div className="flex items-center gap-2 sm:gap-4 xl:gap-6">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="/logo.png"
                alt="AcuSightPro Logo"
                className="h-14 sm:h-16 xl:h-20 w-auto"
                data-testid="logo-image"
              />
            </Link>
            <div className="text-[10px] sm:text-xs font-semibold tracking-wider sm:tracking-widest text-gray-600">
              <span className="hidden sm:inline">BROUGHT TO YOU BY </span>
              <a
                href="https://encoresvcsllc.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline inline-flex items-center gap-1 sm:gap-2"
              >
                <span className="gold-wave-text">ENCORE SERVICES LLC</span>
                <img 
                  src="/encorelogo.png" 
                  alt="Encore Services LLC" 
                  className="h-3 sm:h-4 w-auto"
                />
              </a>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-8">
            <div className="flex gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-sm font-medium transition-colors relative whitespace-nowrap ${
                    isActive(link.href)
                      ? 'text-gray-900 after:absolute after:bottom-[-0.5rem] after:left-0 after:right-0 after:h-0.5 after:bg-gradient-to-r after:from-purple-600 after:to-blue-600 after:rounded-full'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
            <Link
              to="/contact"
              className={`px-5 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/50 transition-all hover:-translate-y-0.5 whitespace-nowrap ${
                isActive('/contact') ? 'ring-2 ring-purple-300 ring-offset-2' : ''
              }`}
              data-testid="contact-button"
            >
              Contact us
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="xl:hidden p-2 text-gray-700 hover:text-gray-900"
            aria-label="Toggle menu"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="xl:hidden bg-white border-t border-gray-200 shadow-lg" data-testid="mobile-menu">
          <nav className="flex flex-col">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`px-6 py-4 text-sm font-medium border-b border-gray-100 ${
                  isActive(link.href)
                    ? 'text-purple-700 bg-purple-50'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              className={`px-6 py-4 text-sm font-medium ${
                isActive('/contact')
                  ? 'text-purple-700 bg-purple-50'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
